/**
 * Per-layer hide list and readable labels for database columns, on the Details
 * panel and the Attributes table only.
 *
 * The hide lists and the labels below were decided by the product owner on
 * 2026-09-14, layer by layer, from the live registry — they are transcribed
 * here verbatim and are not this module's to second-guess. Three labels
 * (`l_type`, `area`, `stype`) were proposed without the column's values being
 * checked and accepted as they stand; they are the ones to revisit if a
 * customer reads them and disagrees.
 *
 * **Display-only.** WFS downloads and QGIS still deliver every column GeoServer
 * serves; hiding a column there is a GeoServer feature-type setting, not code,
 * and out of scope for the viewer. This module only decides what the browser
 * renders.
 *
 * `gid` never appears in any list because GeoServer never serves it — there is
 * nothing to hide.
 *
 * A layer absent from `TABLES` below still gets the global list: these seven
 * are data-entry audit columns and were found in 11 of the 17 layers GeoServer
 * serves, so a layer the registry has never heard of is assumed to carry them
 * too rather than shown with nothing hidden.
 *
 * Matching follows the exact convention `layerGroup()` uses in `layerNames.ts`
 * (see that module's header): try, in order, the unqualified `name`, the full
 * `name`, the unqualified `title`, and the `title`, each through the same
 * whitespace-collapsing lowercase `lookupKey`. This is required because three
 * different strings can name the same layer — the mock proxy's own convention
 * (`mining:tbl08_assets_vm`), the pre-2026-09-09 production convention (the
 * human string advertised as `<Name>`, still live in production today), and
 * the post-rename convention (an XML-valid lowercase slug as `<Name>`, the
 * human string as `<Title>`) — so every table entry below lists all of its
 * aliases, the same shape as `GROUPS` in `layerNames.ts`.
 */
import { lookupKey, unqualified } from './layerNames'

/** Data-entry audit columns, hidden for every layer, known or unknown. */
const GLOBAL_HIDDEN = [
  'mrfguid',
  'create_by',
  'edit_by',
  'create_dt',
  'edit_dt',
  'upload_dt',
  'delete_dt',
] as const

/**
 * Columns that recur across source tables, read the same everywhere so a
 * customer never sees two words for one thing. A per-table label wins over
 * these; these win over the fallback.
 */
const SHARED_LABELS: Readonly<Record<string, string>> = {
  country: 'Country',
  state_prov: 'Province / State',
  status: 'Status',
  owners: 'Owners',
  region: 'Region',
  source: 'Source',
}

interface TableEntry {
  aliases: readonly string[]
  hidden: readonly string[]
  /** Raw column name → label the browser shows; unlisted keys fall back. */
  labels: Readonly<Record<string, string>>
}

const TABLES: readonly TableEntry[] = [
  {
    aliases: [
      'canadian_claims',
      'canadian_claims_trial',
      'Canadian claims',
      'Canadian claims (trial)',
      'canada_claims_subscriber',
      'group_pub_tbl01_claims_canadian',
    ],
    hidden: [],
    labels: {
      claimno: 'Claim number',
      ownerref: 'Registered owner',
      c_type: 'Claim type',
      c_status: 'Claim status',
      l_type: 'Land type',
      date_grant: 'Date granted',
      dateexpiry: 'Expiry date',
      days_old: 'Claim age (days)',
      days_exp: 'Days to expiry',
      c_group: 'Claim group',
    },
  },
  {
    aliases: [
      'mining_assets_cad',
      'mining_assets_usa',
      'mining_assets_trial',
      'Mining assets (CAD)',
      'Mining assets (USA)',
      'Mining assets (trial)',
      'Mining assets (Trial)',
      'tbl08_assets_vm',
    ],
    hidden: ['id', 'assetno', 'propno', 'displayfg', 'latitude', 'longitude', 'public', 'propertyid', 'assetid'],
    labels: {
      asset_name: 'Asset name',
      assetalias: 'Also known as',
      asset_type: 'Asset type',
      dev_stage: 'Development stage',
      commodities: 'Commodities',
      area: 'Mining area',
      accuracy: 'Location accuracy',
      property: 'Property',
    },
  },
  {
    aliases: [
      'property_outline',
      'property_outline_colored_cad',
      'property_outline_colored_usa',
      'property_outline_trial',
      'Property outline',
      'Property outline colored (CAD)',
      'Property outline colored (USA)',
      'Property outline (coloured by owner)',
      'Property outline (trial)',
      'Property outline  (trial)',
    ],
    hidden: ['prop_loc', 'area_ha', 'propertyid', 'propno', 'fill_hex_code'],
    labels: {
      prop_name: 'Property name',
      prop_url: 'Property website',
      map_area: 'Map area',
      company_name_1: 'Owner 1',
      ownership_percent_1: 'Owner 1 share (%)',
      is_operator_1: 'Owner 1 is operator',
      ownership_type_1: 'Owner 1 ownership type',
      company_name_2: 'Owner 2',
      ownership_percent_2: 'Owner 2 share (%)',
      ownership_type_2: 'Owner 2 ownership type',
    },
  },
  {
    aliases: ['map_areas_cad', 'map_areas_usa', 'Map areas (CAD)', 'Map areas (USA)', 'tbl40_map_areas'],
    hidden: ['areano', 'map_code', 'area_type', 'country', 'centroid_x', 'centroid_y', 'area_id'],
    labels: { area_name: 'Area name' },
  },
  {
    aliases: ['protected_areas_cad', 'protected_areas_usa', 'Protected areas (CAD)', 'Protected areas (USA)'],
    hidden: ['iucn_cat', 'dt_source', 'usertype'],
    labels: {
      pname: 'Name',
      stype: 'Site type',
      authority: 'Managing authority',
    },
  },
  {
    aliases: ['canadian_regional_geology', 'Canadian Regional geology'],
    hidden: [
      'objectid',
      'orig_id',
      'unit_code',
      'rock_code',
      'rgb_code',
      'hex_code',
      'update_dt',
      'integra_dt',
      'shape_leng',
      'shape_area',
      'legend_id',
    ],
    labels: {
      group_name: 'Group',
      seque_name: 'Sequence',
      era: 'Era',
      period: 'Period',
      rock_cat: 'Rock category',
      rock_name1: 'Rock type 1',
      rock_name2: 'Rock type 2',
      rock_name3: 'Rock type 3',
      r_desc_sh: 'Short description',
      desc_l_fr: 'Description (FR)',
      desc_l_en: 'Description (EN)',
      geol_prov: 'Geological province',
      legend_nam: 'Legend unit',
    },
  },
  {
    aliases: ['provincial_geology_cad', 'provincial_geology_usa', 'Provincial geology (CAD)', 'Provincial geology (USA)'],
    hidden: [
      'main_id',
      'orgi_id',
      'litho_c_1',
      'eon',
      'r_texture',
      'rmin_na',
      'hex',
      'rgb',
      'update_d',
      'inte_date',
      'litho_c_2',
      'publish_d',
      'unit_c',
    ],
    labels: {
      major_l_1: 'Major lithology 1',
      major_l_2: 'Major lithology 2',
      group_na: 'Group',
      format_na: 'Formation',
      seque_na: 'Sequence',
      complex_n: 'Complex',
      era: 'Era',
      period: 'Period',
      series: 'Series',
      chro_from: 'Age from (Ma)',
      chro_to: 'Age to (Ma)',
      r_desc_sh: 'Short description',
      desc_l_fr: 'Description (FR)',
      desc_l_en: 'Description (EN)',
      geol_prov: 'Geological province',
      country_n: 'Country',
      state_na: 'Province / State',
      scale: 'Map scale',
      reference: 'Reference',
    },
  },
  {
    aliases: ['usa_claims', 'USA claims'],
    hidden: ['count_', 'mtrs', 'shape_leng', 'shape_area', 'notes1', 'notes2'],
    // Every column is hidden: nothing left to label.
    labels: {},
  },
]

const TABLE_INDEX: Record<string, TableEntry> = Object.fromEntries(
  TABLES.flatMap((entry) => entry.aliases.map((alias) => [lookupKey(alias), entry])),
)

/** The table entry a layer resolves to — undefined when unrecorded. */
function tableOf(name: string, title: string): TableEntry | undefined {
  for (const key of [unqualified(name), name, unqualified(title), title]) {
    const hit = key ? TABLE_INDEX[lookupKey(key)] : undefined
    if (hit) return hit
  }
  return undefined
}

/** The extra, per-table hidden keys for a layer — empty when unrecorded. */
function extraHidden(name: string, title: string): readonly string[] {
  return tableOf(name, title)?.hidden ?? []
}

/**
 * Last resort for a column nobody has labelled: underscores become spaces and
 * the first letter is capitalised, so a new column reads as words rather than
 * as a database identifier. Never returns the raw key for a snake_case name.
 */
function fallbackLabel(key: string): string {
  const words = key.replace(/_+/g, ' ').trim()
  // Nothing but underscores: the raw key is the only honest header.
  if (!words) return key
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Own-property lookup: a column named `toString` must not find Object's. */
function own(table: Readonly<Record<string, string>>, key: string): string | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined
}

/**
 * The set of property keys to hide for a layer, on the Details panel and the
 * Attributes table. Always includes the global list; a layer recorded in
 * `TABLES` adds its own.
 */
export function hiddenAttributes(name: string, title = ''): ReadonlySet<string> {
  return new Set([...GLOBAL_HIDDEN, ...extraHidden(name, title)])
}

/**
 * The label the browser shows for a column of a layer, on the Details panel
 * and the Attributes table. Per-table decision first, then the shared
 * vocabulary, then the readable fallback — never the raw column name.
 *
 * Display-only, like the hide list: sorting, e2e selectors and every request
 * to the proxy keep using the raw key.
 */
export function attributeLabel(name: string, title: string, key: string): string {
  const table = tableOf(name, title)
  return (table && own(table.labels, key)) ?? own(SHARED_LABELS, key) ?? fallbackLabel(key)
}

/**
 * `properties` minus its hidden keys, for a layer. Pure — does not mutate the
 * input.
 */
export function visibleProperties(
  name: string,
  title: string,
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const hidden = hiddenAttributes(name, title)
  return Object.fromEntries(Object.entries(properties).filter(([key]) => !hidden.has(key)))
}
